/* import __COLOCATED_TEMPLATE__ from './fin-readiness-bar.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type Store from '@ember-data/store';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  reportState: ReportState;
}

const QUERY_TYPE_STRINGS: Record<string, QueryTypeStrings> = {
  informational: {
    label: 'automation.ai-insights.fin-readiness.your-conversation-volume.breakdown.informational',
    description:
      'automation.ai-insights.fin-readiness.your-conversation-volume.breakdown.informational-description',
    bgColor: 'bg-vis-orange-50',
  },
  personalized: {
    label: 'automation.ai-insights.fin-readiness.your-conversation-volume.breakdown.personalized',
    description:
      'automation.ai-insights.fin-readiness.your-conversation-volume.breakdown.personalized-description',
    bgColor: 'bg-vis-violet-70',
  },
  actions: {
    label: 'automation.ai-insights.fin-readiness.your-conversation-volume.breakdown.actions',
    bgColor: 'bg-vis-teal-50',
    description:
      'automation.ai-insights.fin-readiness.your-conversation-volume.breakdown.actions-description',
  },
  other: {
    label: '',
    bgColor: 'bg-neutral-container-emphasis',
    description: '',
  },
};

interface QueryTypeStrings {
  label: string;
  description: string;
  bgColor: string;
}

export default class FinReadinessBar extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;
  @service declare intl: IntlService;

  @use counterResource = staticDataResource(this, {
    serializedChart: () => this.queryTypeBreakdownChartDefinition,
    reportState: () => this.args.reportState,
  });

  get hasQueryTypeFilter() {
    return {
      type: 'exists',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
      },
    };
  }

  get queryTypesData() {
    return Object.keys(QUERY_TYPE_STRINGS)
      .map((key) => {
        if (key === 'other') {
          return {
            label: QUERY_TYPE_STRINGS[key].label,
            description: QUERY_TYPE_STRINGS[key].description,
            bgColor: QUERY_TYPE_STRINGS[key].bgColor,
            percentage: this.breakdownValue['complex'].percentage.toFixed(0),
            percentageLabel: '',
            rawValue: this.breakdownValue['complex'].raw,
          };
        }
        return {
          label: this.intl.t(QUERY_TYPE_STRINGS[key].label),
          description: this.intl.t(QUERY_TYPE_STRINGS[key].description),
          bgColor: QUERY_TYPE_STRINGS[key].bgColor,
          percentage: this.breakdownValue[key].percentage.toFixed(0),
          percentageLabel: `${this.breakdownValue[key].percentage.toFixed(0)}%`,
          rawValue: this.breakdownValue[key].raw,
        };
      })
      .filter(Boolean); // Remove null entries
  }

  get gridTemplateColumns() {
    let spans = this.queryTypesData.map((item) => item.percentage);
    return spans.map((span) => `${span}fr`).join(' ');
  }

  get totalConversations() {
    return this.counterResource.flatData[0].reduce((acc, [, value]) => acc + (value as number), 0);
  }

  get queryTypeBreakdownChartDefinition() {
    return {
      visualization_type: 'horizontal_bar',
      stacked: true,
      view_by: 'conversation_custom_fields#ai_insights_query_type',
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.hasQueryTypeFilter],
          },
        },
      ],
    };
  }

  get breakdownValue() {
    if (!this.counterResource.flatData || !this.counterResource.flatData[0]) {
      // Fallback data for development/testing
      return {
        informational: { raw: 600, percentage: 60 },
        personalized: { raw: 200, percentage: 20 },
        actions: { raw: 100, percentage: 10 },
        complex: { raw: 100, percentage: 10 },
      };
    }

    return Object.fromEntries(
      this.counterResource.flatData[0].map(([key, value]) => [
        key,
        {
          raw: value,
          percentage: ((value as number) / this.totalConversations) * 100,
        },
      ]),
    );
  }

  get isLoading() {
    return this.counterResource.isLoading;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessBar': typeof FinReadinessBar;
  }
}
